import { format } from "date-fns";

/**
 * Convert Ghost image URLs to optimized format with size parameter
 */
export function optimizeGhostImage(url: string | null, width?: number): string {
  if (!url) return '';
  
  // If it's not a Ghost URL, return as-is
  if (!url.includes('blog.carbonwize.io') && !url.includes('/content/images/')) {
    return url;
  }
  
  // Ghost's image resizing: insert /size/w{width}/ after /content/images/
  if (width && !url.includes('/size/')) {
    return url.replace('/content/images/', `/content/images/size/w${width}/`);
  }
  
  return url;
}

/**
 * Rewrite internal Ghost blog links to the new /blog/ path
 * and add target="_blank" rel="noopener noreferrer" to external links
 */
export function rewriteInternalLinks(html: string): string {
  if (!html) return '';
  
  // First, rewrite internal Ghost blog links
  let processedHtml = html.replace(
    /href=["']https?:\/\/blog\.carbonwize\.io\/([^"']+)["']/g,
    (match, slug) => {
      const cleanSlug = slug.replace(/\/$/, '');
      return `href="/blog/${cleanSlug}"`;
    }
  );
  
  // Add target="_blank" and rel="noopener noreferrer" to external links
  // Match href starting with http(s) that don't already have target attribute
  processedHtml = processedHtml.replace(
    /<a\s+([^>]*?)href=["'](https?:\/\/[^"']+)["']([^>]*?)>/gi,
    (match, before, url, after) => {
      // Skip if it's an internal carbonwize.io link (not blog subdomain)
      if (url.includes('carbonwize.io') && !url.includes('blog.carbonwize.io')) {
        return match;
      }
      // Skip if already has target attribute
      if (before.includes('target=') || after.includes('target=')) {
        return match;
      }
      return `<a ${before}href="${url}"${after} target="_blank" rel="noopener noreferrer">`;
    }
  );
  
  return processedHtml;
}

/**
 * Convert Ghost URL to internal path
 */
export function getInternalUrl(url: string): string {
  if (!url) return '/blog';
  
  // Handle Ghost blog URLs
  const ghostMatch = url.match(/https?:\/\/blog\.carbonwize\.io\/(.+)/);
  if (ghostMatch) {
    const slug = ghostMatch[1].replace(/\/$/, ''); // Remove trailing slash
    return `/blog/${slug}`;
  }
  
  // If it's already an internal URL, return as-is
  if (url.startsWith('/')) {
    return url;
  }
  
  return '/blog';
}

/**
 * Convert Ghost URL to internal path with language prefix
 */
export function getLocalizedInternalUrl(url: string, language: 'en' | 'th'): string {
  const internalPath = getInternalUrl(url);
  if (language === 'en') {
    return `/en${internalPath}`;
  }
  return internalPath;
}

/**
 * Format date for blog display
 */
export function formatBlogDate(date: string, formatStr: string = 'MMMM d, yyyy'): string {
  if (!date) return '';
  
  try {
    return format(new Date(date), formatStr);
  } catch {
    return '';
  }
}

/**
 * Calculate estimated reading time if not provided
 */
export function calculateReadingTime(content: string, providedTime?: number): number {
  if (providedTime && providedTime > 0) {
    return providedTime;
  }
  
  if (!content) return 1;
  
  // Average reading speed: 200 words per minute
  const wordsPerMinute = 200;
  const wordCount = content.replace(/<[^>]+>/g, '').split(/\s+/).length;
  const readingTime = Math.ceil(wordCount / wordsPerMinute);
  
  return Math.max(1, readingTime);
}

/**
 * Generate JSON-LD structured data for a blog post
 */
export function generateArticleJsonLd(post: {
  title: string;
  excerpt: string;
  featureImage?: string | null;
  publishedAt: string;
  updatedAt?: string;
  slug: string;
  primaryAuthor: {
    name: string;
    slug: string;
    profileImage?: string | null;
  };
}) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": post.title,
    "description": post.excerpt,
    "image": post.featureImage || undefined,
    "datePublished": post.publishedAt,
    "dateModified": post.updatedAt || post.publishedAt,
    "author": {
      "@type": "Person",
      "name": post.primaryAuthor.name,
      "url": `https://carbonwize.io/blog/author/${post.primaryAuthor.slug}`,
    },
    "publisher": {
      "@type": "Organization",
      "name": "Carbonwize",
      "logo": {
        "@type": "ImageObject",
        "url": "https://carbonwize.io/lovable-uploads/8d56f73e-d357-4ec7-bd6a-9832088bff37.png",
      },
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://carbonwize.io/blog/${post.slug}`,
    },
  };
}

/**
 * Generate JSON-LD for breadcrumbs
 */
export function generateBreadcrumbJsonLd(items: Array<{ name: string; url: string }>) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": `https://carbonwize.io${item.url}`,
    })),
  };
}
